import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useUserSettings } from "@services/UserSettings";
import { Settings } from "@/globals";
import { FeatureStatus } from "@domain/General/Enums/FeatureStatus";
import { ConciergeMessage } from "./ConciergeMessage";
import { FeatureType } from "../WorkOrders/FeatureType";
import type { IConciergeMessage } from "./IConciergeMessage";
import type { ICarbucksRewardsFeature, ICarbucksRewardsFeatureDto } from "./ICarbucksRewardsFeature";
import type { IWorkOrderConfigurationDto } from "../WorkOrders/IWorkOrderConfiguration";

export class CarbucksRewardsFeature implements ICarbucksRewardsFeature
{

    isEnabledInAppSettings: boolean;
    hasCarbucksRewards: boolean;
    subscriberNumber?: string;
    title: string;
    subTitle: string;
    provider: string;
    termsAndConditionsLinkUrl?: string;
    phoneNumber: string;
    termInMonths: number;
    dateActiveFrom?: string | Dayjs;
    dateActiveTo?: string | Dayjs;
    status: FeatureStatus;

    isRemindersEnabled: boolean;
    isCashbackEnabled: boolean;
    isAutomotiveCashbackEnabled: boolean;
    isConciergeEnabled: boolean;
    isConciergeMessagesEnabled: boolean;
    carbucksAppTitle: string;
    remindersTitle: string;
    cashbackTitle: string;
    conciergeTitle: string;
    conciergePhoneNumber: string;
    conciergeHours: string;
    showAsProductInApp: boolean;
    
    workOrderConfigurations: IWorkOrderConfigurationDto[];
    
    operatingHours: string;
    messages: IConciergeMessage[] = [];

    get type() { return FeatureType.Rewards }
    
    constructor( model: ICarbucksRewardsFeatureDto )
    {

        this.isEnabledInAppSettings = model.isEnabledInAppSettings;
        this.hasCarbucksRewards = model.hasCarbucksRewards;
        this.subscriberNumber = model.subscriberNumber;
        this.title = model.title;
        this.subTitle = model.title;
        this.provider = model.provider;
        this.termsAndConditionsLinkUrl = model.termsAndConditionsLinkUrl;
        this.phoneNumber = model.phoneNumber;
        this.termInMonths = model.termInMonths;
        this.dateActiveFrom = model.dateActiveFrom == null ? undefined : dayjs( model.dateActiveFrom );
        this.dateActiveTo = model.dateActiveTo == null ? undefined : dayjs( model.dateActiveTo );
        this.status = model.status;

        this.isRemindersEnabled = model.isRemindersEnabled;
        this.isCashbackEnabled = model.isCashbackEnabled;
        this.isAutomotiveCashbackEnabled = model.isAutomotiveCashbackEnabled;
        this.isConciergeEnabled = model.isConciergeEnabled;
        this.isConciergeMessagesEnabled = model.isConciergeMessagesEnabled;
        this.carbucksAppTitle = model.carbucksAppTitle;
        this.remindersTitle = model.remindersTitle;
        this.cashbackTitle = model.cashbackTitle;
        this.conciergeTitle = model.conciergeTitle;
        this.conciergePhoneNumber = model.conciergePhoneNumber;
        this.conciergeHours = model.conciergeHours;
        this.showAsProductInApp = model.showAsProductInApp;

        this.workOrderConfigurations = model.workOrderConfigurations;

        this.messages = model.messages.map( m => new ConciergeMessage( m ) );
        this.operatingHours = ( model.conciergeHours ?? '' ).trim().length > 0 
            ? model.conciergeHours 
            : Settings.Concierge.HoursOfOperation;

    }

    /**
     * True if the feature is enabled.  This is NOT the same as the feature being active
     */
    get isEnabled()
    {
        return this.hasCarbucksRewards && this.status > FeatureStatus.Draft;
    }

    /**
     * True if the feature is pending the consumer acknowledgement.  
     */
    get isPending()
    {
        return this.hasCarbucksRewards && this.status == FeatureStatus.Pending;
    }
    
    /**
     * True if the feature is active.  
     */
    get isActive()
    {
        return this.hasCarbucksRewards && this.status == FeatureStatus.Active;
    }

    /**
     * True if the feature is active.  
     */
    get isInactive()
    {
        return this.hasCarbucksRewards 
        && ( this.status == FeatureStatus.Expired || this.status == FeatureStatus.Cancelled || this.status == FeatureStatus.Voided );
    }

    /**
     * True if the concierge messages are enabled
     */
    get isMessagesEnabled()
    {
        const userSettings = useUserSettings();
        return this.isEnabledInAppSettings && userSettings.showConciergeMessages;
    }

    /**
     * True if the user has messages from the concierge
     */
    get hasMessages() 
    {
        return this.messages.length > 0;
    }
    
    /**
     * Dismisses the message so that it is removed from the list and wont
     * show again
     * @param message 
     */
    async dismissMessage( message: IConciergeMessage )
    {

        this.messages = this.messages.filter( m => m.conciergeMessageId != message.conciergeMessageId );
        // TODO: dismiss a message using its conciergeMessageId

    }
    
}